import { PlusOutlined } from '@ant-design/icons'
import { Button, EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import useUser from 'hooks/useUser/useUser'
import useUsersList from 'hooks/useUser/useUsersList'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAbsencesList } from 'screens/Rostering/hooks/useAbsencesList'
import { useAbsencesPastList } from 'screens/Rostering/hooks/useAbsencesPastList'
import { useAbsencesTodayList } from 'screens/Rostering/hooks/useAbsencesTodayList'
import { useAbsencesUpcomingList } from 'screens/Rostering/hooks/useAbsencesUpcomingList'
import { useAbsenceTypesList } from 'screens/Rostering/hooks/useAbsenceTypesList'
import { useExportAbsencesHistoryCsv } from 'screens/Rostering/hooks/useExportAbsencesHistory'
import { styled } from 'styled-components'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../../components'
import AbsenceItem from './AbsenceItem'
import AbsencesFilters from './AbsencesFilters'
import AddIndividualAbsenceModal from './modals/AddIndividualAbsenceModal'

type AllAbsencesScreenProps = {
  all?: boolean
}

const initialFiltersValue: {
  rangePicker: [Date | undefined, Date | undefined]
  absenceType: string | undefined
  users: string[] | undefined
} = {
  rangePicker: [undefined, undefined],
  absenceType: undefined,
  users: undefined
}

const AllAbsencesScreen = ({ all = false }: AllAbsencesScreenProps) => {
  const { t } = useTranslation()
  const [filterParams, setFilterParams] = useState(initialFiltersValue)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { absenceTypes } = useAbsenceTypesList()
  const { users } = useUsersList({})
  const { id: userId } = useParams()
  const { user: currentUser, isLoading: isCurrentUserLoading } = useUser({
    id: userId
  })

  const { rangePicker, absenceType, users: filterUsers } = filterParams

  // Determine which users to export
  const usersForExport = all ? filterUsers : userId ? [userId] : undefined

  const exportFilters = {
    rangePicker,
    absenceType,
    users: usersForExport
  }

  const exportCsv = useExportAbsencesHistoryCsv(exportFilters)
  const hasFilterStartDate = !!rangePicker?.[0]
  const hasFilterEndDate = !!rangePicker?.[1]
  const filteredAbsenceTypes = absenceTypes.filter(
    (absenceType) => !absenceType.isDeleted
  )

  const { absences, isAbsencesLoading } = useAbsencesList({
    users: usersForExport as any,
    absenceType,
    startDate: hasFilterStartDate
      ? dayjs(rangePicker[0]).startOf('day').toISOString()
      : undefined,
    endDate: hasFilterEndDate
      ? dayjs(rangePicker[1]).endOf('day').toISOString()
      : undefined
  })
  const { todayAbsences, isTodayAbsencesLoading } = useAbsencesTodayList({
    users: usersForExport as any,
    absenceType
  })
  const { pastAbsences, isPastAbsencesLoading } = useAbsencesPastList({
    users: usersForExport as any,
    absenceType
  })
  const { upcomingAbsences, isUpcomingAbsencesLoading } =
    useAbsencesUpcomingList({
      users: usersForExport as any,
      absenceType
    })

  const isOtherAbsencesLoading =
    isTodayAbsencesLoading || isPastAbsencesLoading || isUpcomingAbsencesLoading

  const showFilteredAbsences = hasFilterStartDate && hasFilterEndDate
  const isAbsencesEmpty = absences.length === 0
  const isAllAbsencesEmpty =
    todayAbsences.length === 0 &&
    pastAbsences.length === 0 &&
    upcomingAbsences.length === 0

  const mergedAbsences = useMemo(
    () => [...todayAbsences, ...upcomingAbsences],
    [todayAbsences, upcomingAbsences]
  )

  if (isCurrentUserLoading) {
    return <Spinner />
  }

  const activeUsers = users.filter(
    (user) => user.active !== false && user.deleted !== true
  )

  const handleDownloadCsv = () => {
    exportCsv()
  }

  return (
    <>
      <PageHeader
        title={
          all
            ? t('ALL_ABSENCES')
            : `${currentUser?.name.first} ${currentUser?.name.last}'s Absences`
        }
        sticky
      >
        <ButtonsContainer>
          <Button variant="primary" onClick={() => setIsModalVisible(true)}>
            <PlusOutlined />
            {t('ADD_ABSENCE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <AbsencesFilters
          value={filterParams}
          onChange={setFilterParams}
          all={all}
          absenceTypes={filteredAbsenceTypes}
          onDownloadCsv={handleDownloadCsv}
        />

        {/* Loading Spinner */}
        {(isAbsencesLoading || isOtherAbsencesLoading) && <Spinner />}

        {/* Filtered Absences */}
        {showFilteredAbsences &&
          !isAbsencesLoading &&
          !isOtherAbsencesLoading && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {isAbsencesEmpty ? (
                <EmptyState
                  title={t('NO_DATA_TO_SHOW')}
                  description={t('NO_DATA_ABSENCE')}
                  icon={<NoDataIcon />}
                />
              ) : (
                <Section>
                  <SectionHeader title={t('ABSENCES')} />
                  {absences.map((absence) => (
                    <AbsenceItem
                      key={absence.id}
                      absence={absence}
                      user={users.find((user) => user.id === absence.userId)}
                      absenceType={absenceTypes.find(
                        (absenceType) => absenceType.id === absence.absenceType
                      )}
                      all={all}
                    />
                  ))}
                </Section>
              )}
            </>
          )}

        {/* Regular Absences */}
        {!showFilteredAbsences &&
          !isAbsencesLoading &&
          !isOtherAbsencesLoading && (
            <>
              {todayAbsences.length > 0 && (
                <Section>
                  <SectionHeader title={t('TODAY')} />
                  {todayAbsences.map((absence) => (
                    <AbsenceItem
                      key={absence.id}
                      absence={absence}
                      user={users?.find((user) => user.id === absence.userId)}
                      absenceType={absenceTypes?.find(
                        (absenceType) => absenceType.id === absence.absenceType
                      )}
                      all={all}
                    />
                  ))}
                </Section>
              )}
              {upcomingAbsences.length > 0 && (
                <Section>
                  <SectionHeader title={t('UPCOMING')} />
                  {upcomingAbsences.map((absence) => (
                    <AbsenceItem
                      key={absence.id}
                      absence={absence}
                      user={users.find((user) => user.id === absence.userId)}
                      absenceType={absenceTypes.find(
                        (absenceType) => absenceType.id === absence.absenceType
                      )}
                      all={all}
                    />
                  ))}
                </Section>
              )}
              {pastAbsences.length > 0 && (
                <Section>
                  <SectionHeader title={t('PAST')} />
                  {pastAbsences.map((absence) => (
                    <AbsenceItem
                      key={absence.id}
                      absence={absence}
                      user={users.find((user) => user.id === absence.userId)}
                      absenceType={absenceTypes.find(
                        (absenceType) => absenceType.id === absence.absenceType
                      )}
                      all={all}
                    />
                  ))}
                </Section>
              )}
              {isAllAbsencesEmpty && (
                <EmptyState
                  title={t('NO_DATA_TO_SHOW')}
                  description={t('NO_DATA_ABSENCE')}
                  icon={<NoDataIcon />}
                />
              )}
            </>
          )}
      </PageWrapper>
      {isModalVisible && (
        <AddIndividualAbsenceModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          all={all}
          users={activeUsers}
          absences={mergedAbsences}
        />
      )}
    </>
  )
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default AllAbsencesScreen
